import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createReferral, fetchUserReferrals } from "../actions/referralActions";
import { FaCopy, FaCheck, FaLink, FaUserPlus, FaChartLine, FaWallet } from "react-icons/fa";
import formatMoney from "../components/formatMoney";
import "../styles/createReferral.css";
import { getReferralLink, SITE_NAME_FULL } from "../variables";

const CreateReferral = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { userReferrals = [] } = useSelector((state) => state.referral || {});
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [createdReferral, setCreatedReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    document.title = `Referral - ${SITE_NAME_FULL}`;
    
    // Check if user is logged in
    if (!user || !user.user_id) {
      navigate("/signin", { state: { redirect: "/create-referral" } });
      return;
    }

    // Fetch user's existing referrals
    dispatch(fetchUserReferrals());

    // Set default name to user's name
    if (user.name || user.username) {
      setName(user.name || user.username);
    }
  }, [user, navigate, dispatch]);

  const generateCode = () => {
    // Generate a unique code based on username and random string
    const username = user?.username || "user";
    const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
    const generatedCode = `${username.toUpperCase()}${randomStr}`;
    setCode(generatedCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      alert("Please enter a referral code");
      return;
    }

    if (!name.trim()) {
      alert("Please enter your name");
      return;
    }

    setIsLoading(true);
    try {
      const referralData = await dispatch(
        createReferral({
          code: code.trim().toUpperCase(),
          name: name.trim(),
          percentage: 10.00,
        })
      );
      setCreatedReferral(referralData);
      // Refresh the referrals list
      await dispatch(fetchUserReferrals());
      // Reset form
      setCode("");
      // Optionally redirect to dashboard with code in URL after a short delay
      // Uncomment the line below if you want auto-redirect after creation
      // setTimeout(() => navigate(`/referral?code=${referralData.code}`), 2000);
    } catch (error) {
      console.error("Error creating referral:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyReferralLink = (link, code) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  if (!user || !user.user_id) {
    return null; // Will redirect
  }

  return (
    <div className="create-referral">
      <div className="create-referral-container">
        <h1 className="create-referral-title">Referral</h1>
        <p className="create-referral-subtitle">
          Create your referral link and earn 10% commission on every order made through your link!
        </p>

        {/* Display existing referrals */}
        {userReferrals.length > 0 && (
          <div className="existing-referrals-section" style={{ marginBottom: "2rem" }}>
            <h2 style={{ marginBottom: "1rem", fontSize: "1.5rem", fontWeight: 600 }}>
              Your Referral Codes
            </h2>
            <div className="referrals-list" style={{ 
              display: "grid", 
              gap: "1rem",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))"
            }}>
              {userReferrals.map((referral) => {
                const referralLink = getReferralLink(referral.code);
                return (
                  <div 
                    key={referral.referral_id} 
                    className="referral-card"
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "1.5rem",
                      backgroundColor: "#fff",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  >
                    <div style={{ marginBottom: "1rem" }}>
                      <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1.2rem", fontWeight: 600 }}>
                        {referral.name}
                      </h3>
                      <div style={{ fontSize: "0.9rem", color: "#666", marginBottom: "0.5rem" }}>
                        Code: <strong>{referral.code}</strong>
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "#666", marginBottom: "0.5rem" }}>
                        Commission: <strong>{referral.percentage || 10}%</strong>
                      </div>
                      <div style={{ 
                        fontSize: "0.9rem", 
                        color: "#666",
                        padding: "0.75rem",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "4px",
                        marginTop: "0.75rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                      }}>
                        <FaWallet style={{ color: "#17a2b8" }} />
                        <span>Balance: <strong style={{ color: "#17a2b8", fontSize: "1rem" }}>
                          {formatMoney(referral.remaining_amount || 0)}
                        </strong></span>
                      </div>
                    </div>
                    <div style={{ marginBottom: "1rem" }}>
                      <label style={{ fontSize: "0.85rem", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                        Referral Link:
                      </label>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <input
                          type="text"
                          value={referralLink}
                          readOnly
                          style={{
                            flex: 1,
                            padding: "0.5rem",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "0.85rem"
                          }}
                        />
                        <button
                          onClick={() => copyReferralLink(referralLink, referral.code)}
                          style={{
                            padding: "0.5rem 1rem",
                            border: "none",
                            borderRadius: "4px",
                            backgroundColor: "#007bff",
                            color: "#fff",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem"
                          }}
                          title="Copy link"
                        >
                          {copiedCode === referral.code ? <FaCheck /> : <FaCopy />}
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/referral?code=${referral.code}`)}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor: "#28a745",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem"
                      }}
                    >
                      <FaChartLine /> View Dashboard
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!createdReferral ? (
          <form onSubmit={handleSubmit} className="create-referral-form">
            <div className="form-group">
              <label htmlFor="name">Your Name *</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="code">Referral Code *</label>
              <div className="code-input-group">
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Enter or generate a referral code"
                  required
                  className="form-input"
                  maxLength={20}
                />
                <button
                  type="button"
                  onClick={generateCode}
                  className="generate-code-btn"
                >
                  Generate
                </button>
              </div>
              <small className="form-hint">
                Use only letters and numbers. Code will be converted to uppercase.
              </small>
            </div>

            <div className="form-info">
              <p>
                <strong>Default Commission:</strong> 10% of each order made through your referral link
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="submit-btn"
            >
              {isLoading ? "Creating..." : "Create Referral Link"}
            </button>
          </form>
        ) : (
          <div className="referral-success">
            <div className="success-icon">
              <FaUserPlus />
            </div>
            <h2>Referral Created Successfully!</h2>
            <div className="referral-link-section">
              <label>Your Referral Link:</label>
              <div className="link-display">
                <input
                  type="text"
                  value={createdReferral.link}
                  readOnly
                  className="link-input"
                />
                <button
                  onClick={() => copyToClipboard(createdReferral.link)}
                  className="copy-btn"
                  title="Copy link"
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </button>
              </div>
            </div>
            <div className="referral-info-section">
              <div className="info-card">
                <span className="info-label">Referral Code:</span>
                <span className="info-value">{createdReferral.code}</span>
              </div>
              <div className="info-card">
                <span className="info-label">Commission Rate:</span>
                <span className="info-value">{createdReferral.percentage}%</span>
              </div>
            </div>
            <div className="earnings-link-section">
              <p>
                <FaLink className="link-icon" />
                <strong>Check your earnings:</strong>{" "}
                <button
                  onClick={() => navigate("/referral")}
                  className="earnings-link-btn"
                >
                  View Referral Dashboard
                </button>
              </p>
            </div>
            <div className="action-buttons">
              <button
                onClick={() => navigate(`/referral?code=${createdReferral.code}`)}
                className="view-dashboard-btn"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => {
                  setCreatedReferral(null);
                  setCode("");
                }}
                className="create-another-btn"
              >
                Create Another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateReferral;

